import { memo, useState } from 'react';
import { Modal } from '@tw/tw-component-library';
import styled from 'styled-components';

// ============= Styled Components =============
const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
  }

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  span {
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.text.primary};
    user-select: none;
  }
`;

const ActionButton = styled.button<{
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  background-color: ${({ theme, variant, disabled }) =>
    disabled
      ? theme.colors.grays.gray1
      : variant === 'primary'
      ? theme.colors.primary
      : theme.colors.grays.gray1};
  color: ${({ theme }) => theme.colors.text.onDark ?? theme.colors.background};

  &:hover:not(:disabled) {
    background-color: ${({ theme, variant }) =>
      variant === 'primary' ? theme.colors.secondary : theme.colors.text.secondary};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

// ============= Types =============
export interface PrintOptions {
  includeComments: boolean;
  includeAttachments: boolean;
  includeActivities: boolean;
  includePeople: boolean;
}

interface PrintOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (options: PrintOptions) => void;
  isPrinting?: boolean;
}

// ============= Component =============
/**
 * PrintOptionsModal Component
 * Modal for selecting print options before printing workflow
 */
export const PrintOptionsModal = memo<PrintOptionsModalProps>(
  ({ isOpen, onClose, onPrint, isPrinting = false }) => {
    const [options, setOptions] = useState<PrintOptions>({
      includeComments: true,
      includeAttachments: true,
      includeActivities: true,
      includePeople: true,
    });

    const handleCheckboxChange = (key: keyof PrintOptions) => {
      setOptions((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };

    const handlePrint = () => {
      onPrint(options);
    };

    const handleCancel = () => {
      // Reset options on cancel
      setOptions({
        includeComments: false,
        includeAttachments: false,
        includeActivities: false,
        includePeople: false,
      });
      onClose();
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        title="Print Options"
        size="small"
      >
        <div style={{ padding: '1rem 0' }}>
          <CheckboxContainer>
            <CheckboxItem>
              <input
                type="checkbox"
                checked={options.includeComments}
                onChange={() => handleCheckboxChange('includeComments')}
                disabled={isPrinting}
              />
              <span>Include Request Comments</span>
            </CheckboxItem>
            <CheckboxItem>
              <input
                type="checkbox"
                checked={options.includeAttachments}
                onChange={() => handleCheckboxChange('includeAttachments')}
                disabled={isPrinting}
              />
              <span>Include request attachment files</span>
            </CheckboxItem>
            <CheckboxItem>
              <input
                type="checkbox"
                checked={options.includeActivities}
                onChange={() => handleCheckboxChange('includeActivities')}
                disabled={isPrinting}
              />
              <span>Include Request activities</span>
            </CheckboxItem>
            <CheckboxItem>
              <input
                type="checkbox"
                checked={options.includePeople}
                onChange={() => handleCheckboxChange('includePeople')}
                disabled={isPrinting}
              />
              <span>Include people working on this request</span>
            </CheckboxItem>
          </CheckboxContainer>
          <ButtonContainer>
            <ActionButton
              onClick={handleCancel}
              variant="secondary"
              disabled={isPrinting}
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handlePrint}
              variant="primary"
              disabled={isPrinting}
            >
              {isPrinting ? 'Printing...' : 'Print'}
            </ActionButton>
          </ButtonContainer>
        </div>
      </Modal>
    );
  }
);

PrintOptionsModal.displayName = 'PrintOptionsModal';
